import { Fragment, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type RefObject } from "react";
import {
  ArrowLeft,
  Camera,
  ChevronLeft,
  ChevronRight,
  Info,
  MessageCirclePlus,
  Mic,
  Paperclip,
  Plus,
  Pause,
  Play,
  Send,
  SmilePlus,
  Trash2,
  Users,
  Video,
  X,
  Zap,
} from "lucide-react";
import { formatMeetingWhen, type ChatMeeting } from "@/lib/meetingsApi";
import {
  ChatEmojiGifPicker,
  extractLoneMediaUrl,
  type ChatGifItem,
} from "@/components/ChatEmojiGifPicker";
import { ChatBrandMark } from "@/components/ChatLogo";
import { ChatMessageText } from "@/components/ChatMessageText";
import { loneMessageEmojis } from "@/lib/chatNotoEmoji";
import { ChatMentionPicker } from "@/components/ChatMentionPicker";
import { MessageReactionChips } from "@/components/MessageContextMenu";
import { MessageHoverActions } from "@/components/MessageHoverActions";
import { MessageTicks } from "@/components/MessageTicks";
import { TypingIndicator } from "@/components/TypingIndicator";
import { ChatEffectBubbleWrap, ChatSendEffectsMenu } from "@/components/ChatSendEffects";
import { ChatVoiceHoldMic } from "@/components/ChatVoiceHoldMic";
import { ChatVoiceLiveWave } from "@/components/ChatVoiceLiveWave";
import { ChatVoicePlayer } from "@/components/ChatVoicePlayer";
import { ChatReplyComposerBar, ChatReplyQuote, chatReplySnippet } from "@/components/ChatReply";
import { AvatarBubble, ClientPill, ConvTypePill } from "@/components/chatChrome";
import type { ChatCallSession } from "@/components/ChatCallOverlay";
import {
  conversationTitle,
  formatChatLastSeen,
  formatChatThreadDayLabel,
  formatChatTime,
  isAudioAttachment,
  isCallLogBody,
  isGuestLobbyBody,
  isMeetingReminderBody,
  isImageAttachment,
  isImageOnlyMessage,
  isVideoAttachment,
  isVideoOnlyMessage,
  memberLabel,
  peerAvatarUrl,
  type ChatMobilePane,
} from "@/lib/chatFormat";
import {
  blobToVoiceFile,
  CHAT_VOICE_MAX_MS,
  CHAT_VOICE_MIN_MS,
  chatVoiceSupported,
  chatVoicePauseSupported,
  createChatVoiceMeter,
  formatVoiceClock,
  startChatVoiceRecorder,
  stopChatVoiceStream,
  type ChatVoiceMeter,
} from "@/lib/chatVoice";
import { expandEmoticonsWithCaret } from "@/lib/emoticons";
import {
  filterMentionCandidates,
  findMentionQuery,
  groupMentionMembers,
  insertMention,
  mentionHighlightLabels,
  mentionLabel,
} from "@/lib/chatMentions";
import { isChatPeerBusyInCall, chatCallIsJoinable, conversationClientPillName, normalizeChatPresence, type ChatAttachment, type ChatColleague, type ChatConversation, type ChatMessage, type ChatMessageEffect } from "@/lib/chatApi";
import { playChatVoiceOpen } from "@/lib/chatNotificationSound";
import { wallpaperBlurPx, type ChatWallpaperPrefs } from "@/lib/theme/chatWallpapers";
import { ChatAttachSourceModal } from "@/components/ChatAttachSourceModal";
import { ChatAttachPickerModal } from "@/components/ChatAttachPickerModal";
import { ChatPhotoFilterSheet } from "@/components/ChatPhotoFilterSheet";
import { ChatVideoComposeSheet } from "@/components/ChatVideoComposeSheet";
import { ChatCaptureSheet } from "@/components/ChatCaptureSheet";
import { ChatPollCard } from "@/components/ChatPollCard";
import { ChatPollComposer } from "@/components/ChatPollComposer";
import { isChatPollMessage, messagePoll } from "@/lib/chatPolls";
import { isComposerImageFile, isFilterableChatPhoto } from "@/lib/chatPhotoFilters";

type ReplyPreview = {
  id: number;
  body: string;
  sender_id: number;
  sender_name?: string | null;
};

type Props = {
  suiteUserId: string;
  selfId: number;
  selfAvatarUrl?: string | null;
  selfLabel?: string;
  mobilePane: ChatMobilePane;
  selected: ChatConversation | null;
  colleagues: ChatColleague[];
  presence: Record<number, string>;
  lastSeen?: Record<number, number>;
  messages: ChatMessage[];
  loadingThread: boolean;
  typingLabel: string | null;
  wallpaperPrefs: ChatWallpaperPrefs;
  threadFileDragOver: boolean;
  callSession: ChatCallSession | null;
  incomingCall: { callId: string } | null;
  suppressJoinCallId: string | null;
  draft: string;
  files: File[];
  showPicker: boolean;
  sendEffectsOpen: boolean;
  replyTo: ChatMessage | null;
  editingMsgId: number | null;
  editDraft: string;
  effectReplay: Record<number, number>;
  flashReplyId: number | null;
  threadPaneRef: RefObject<HTMLElement | null>;
  threadScrollRef: RefObject<HTMLDivElement | null>;
  threadEndRef: RefObject<HTMLDivElement | null>;
  draftRef: RefObject<HTMLTextAreaElement | null>;
  onLeaveToSalon: () => void;
  onOpenAvatarPreview: (url: string | null | undefined, alt?: string) => void;
  onJoinOngoingCall: (conversationId: number, callId: string, media: "audio" | "video", peerLabel: string) => void;
  onStartCall: (media: "audio" | "video") => void;
  onToggleInfo: () => void;
  onThreadDragEnter: (e: DragEvent) => void;
  onThreadDragOver: (e: DragEvent) => void;
  onThreadDragLeave: (e: DragEvent) => void;
  onThreadDrop: (e: DragEvent) => void;
  onOpenMsgMenu: (m: ChatMessage, x: number, y: number) => void;
  onBubblePointerDown: (m: ChatMessage, e: PointerEvent) => void;
  onBubblePointerMove: (e: PointerEvent) => void;
  onClearLongPress: () => void;
  onOpenMediaLightbox: (src: string, alt?: string, attachment?: ChatAttachment) => void;
  onEditDraftChange: (value: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onReact: (messageId: number, emoji: string) => void;
  onBeginReply: (m: ChatMessage) => void;
  onCopyMessage: (m: ChatMessage) => void;
  onForwardMessage: (m: ChatMessage) => void;
  onScrollToMessage: (id: number) => void;
  onReplayEffect: (messageId: number) => void;
  resolveReplyPreview: (m: ChatMessage) => ReplyPreview | null;
  replyAuthorLabel: (m: ChatMessage) => string;
  myReactionOn: (m: ChatMessage) => string | null;
  onRemoveFile: (index: number) => void;
  onAddComposerFiles: (list: File[]) => void;
  onEmoji: (emoji: string) => void;
  onSendGif: (gif: ChatGifItem) => void;
  onClosePicker: () => void;
  onTogglePicker: () => void;
  onCancelReply: () => void;
  onDraftChange: (value: string, caret: number) => void;
  onSend: (effect?: ChatMessageEffect | null) => void;
  onSendVoice: (file: File) => void;
  onSendCapturedPhoto: (file: File, caption: string) => boolean | Promise<boolean>;
  onCreatePoll: (question: string, options: string[]) => void;
  onVotePoll: (messageId: number, optionId: number) => void;
  onClosePoll: (messageId: number) => void;
  pollComposerBusy?: boolean;
  pollComposerError?: string | null;
  onVoiceError: (message: string) => void;
  onOpenSendEffects: () => void;
  onCloseSendEffects: () => void;
  onOpenNewDm: () => void;
  onOpenNewGroup: () => void;
  onOpenNewVideo: () => void;
  aurizReady?: boolean;
  aurizTitle?: string;
  onAuriz?: () => void;
  meetings?: ChatMeeting[];
  onJoinMeeting?: () => void;
};

function voiceMessageFace(
  m: ChatMessage,
  selfId: number,
  selfAvatarUrl: string | null | undefined,
  selfLabel: string | undefined,
  colleagues: ChatColleague[],
  selected: ChatConversation | null
) {
  if (m.sender_id === selfId) {
    return { url: selfAvatarUrl ?? null, label: selfLabel || "Vous" };
  }
  const col = colleagues.find((c) => c.id === m.sender_id);
  const mem = selected?.members?.find((x) => x.id === m.sender_id);
  return {
    url: col?.avatar_url ?? mem?.avatar_url ?? null,
    label:
      col?.display_name ||
      mem?.display_name ||
      m.sender_name ||
      col?.username ||
      mem?.username ||
      "?",
  };
}

export function ChatThread(props: Props) {
  const {
    suiteUserId,
    selfId,
    selfAvatarUrl,
    selfLabel,
    mobilePane,
    selected,
    colleagues,
    presence,
    lastSeen = {},
    messages,
    loadingThread,
    typingLabel,
    wallpaperPrefs,
    threadFileDragOver,
    callSession,
    incomingCall,
    suppressJoinCallId,
    draft,
    files,
    showPicker,
    sendEffectsOpen,
    replyTo,
    editingMsgId,
    editDraft,
    effectReplay,
    flashReplyId,
    threadPaneRef,
    threadScrollRef,
    threadEndRef,
    draftRef,
    onLeaveToSalon,
    onOpenAvatarPreview,
    onJoinOngoingCall,
    onStartCall,
    onToggleInfo,
    onThreadDragEnter,
    onThreadDragOver,
    onThreadDragLeave,
    onThreadDrop,
    onOpenMsgMenu,
    onBubblePointerDown,
    onBubblePointerMove,
    onClearLongPress,
    onOpenMediaLightbox,
    onEditDraftChange,
    onCancelEdit,
    onSaveEdit,
    onReact,
    onBeginReply,
    onCopyMessage,
    onForwardMessage,
    onScrollToMessage,
    onReplayEffect,
    resolveReplyPreview,
    replyAuthorLabel,
    myReactionOn,
    onRemoveFile,
    onAddComposerFiles,
    onEmoji,
    onSendGif,
    onClosePicker,
    onTogglePicker,
    onCancelReply,
    onDraftChange,
    onSend,
    onSendVoice,
    onSendCapturedPhoto,
    onCreatePoll,
    onVotePoll,
    onClosePoll,
    pollComposerBusy = false,
    pollComposerError = null,
    onVoiceError,
    onOpenSendEffects,
    onCloseSendEffects,
    onOpenNewDm,
    onOpenNewGroup,
    onOpenNewVideo,
    aurizReady = false,
    aurizTitle = "",
    onAuriz,
    meetings = [],
    onJoinMeeting,
  } = props;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const sendLongPressTimer = useRef<number | null>(null);
  const sendLongPressFired = useRef(false);
  const skipVoiceUntilRef = useRef(0);
  const voiceRecRef = useRef<MediaRecorder | null>(null);
  const voiceStreamRef = useRef<MediaStream | null>(null);
  const voiceChunksRef = useRef<Blob[]>([]);
  const voiceExtRef = useRef("webm");
  const voiceTickRef = useRef<number | null>(null);
  const voiceStartedAtRef = useRef(0);
  const voiceAccumMsRef = useRef(0);
  const voicePausedRef = useRef(false);
  const voiceMeterRef = useRef<ChatVoiceMeter | null>(null);
  const voiceStartGenRef = useRef(0);
  const voicePreviewRef = useRef<HTMLAudioElement | null>(null);
  const voicePreviewUrlRef = useRef<string | null>(null);
  const [voiceOn, setVoiceOn] = useState(false);
  const [voicePaused, setVoicePaused] = useState(false);
  const [voicePreviewing, setVoicePreviewing] = useState(false);
  const [voiceMeter, setVoiceMeter] = useState<ChatVoiceMeter | null>(null);
  const [voiceMs, setVoiceMs] = useState(0);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceLocked, setVoiceLocked] = useState(false);
  const [draftCaret, setDraftCaret] = useState(0);
  const [mentionHi, setMentionHi] = useState(0);
  const [mentionDismissed, setMentionDismissed] = useState(false);
  const [attachSourceOpen, setAttachSourceOpen] = useState(false);
  const [pollComposerOpen, setPollComposerOpen] = useState(false);
  const [attachPicker, setAttachPicker] = useState<null | "coffre" | "photos">(null);
  const [filterFile, setFilterFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(true);
  const draftWasEmpty = useRef(true);
  const [threadRevealed, setThreadRevealed] = useState(false);
  const seenMsgIds = useRef(new Set<number>());
  const threadPrimed = useRef(false);
  const [enterIds, setEnterIds] = useState<Set<number>>(() => new Set());
  const sending = messages.some((m) => m.pending);
  const imageAttach = files
    .map((file, index) => ({ file, index }))
    .filter(({ file }) => isComposerImageFile(file));
  const otherAttach = files
    .map((file, index) => ({ file, index }))
    .filter(({ file }) => !isComposerImageFile(file));
  const hasImageAttach = imageAttach.length > 0;

  useEffect(() => {
    setThreadRevealed(false);
    seenMsgIds.current = new Set();
    threadPrimed.current = false;
    setEnterIds(new Set());
    setFilterFile(null);
    setVideoFile(null);
    setCaptureOpen(false);
    setToolsOpen(true);
    draftWasEmpty.current = true;
  }, [selected?.id]);

  useEffect(() => {
    const empty = !draft.trim();
    if (!empty && draftWasEmpty.current) {
      setToolsOpen(false);
    } else if (empty && !draftWasEmpty.current) {
      setToolsOpen(true);
    }
    draftWasEmpty.current = empty;
  }, [draft]);

  useEffect(() => {
    if (!threadPrimed.current) {
      for (const m of messages) seenMsgIds.current.add(m.id);
      if (messages.length > 0 || !loadingThread) threadPrimed.current = true;
      return;
    }
    const fresh: number[] = [];
    for (const m of messages) {
      if (seenMsgIds.current.has(m.id)) continue;
      seenMsgIds.current.add(m.id);
      fresh.push(m.id);
    }
    if (fresh.length === 0) return;
    setEnterIds((prev) => {
      const next = new Set(prev);
      for (const id of fresh) next.add(id);
      return next;
    });
    const t = window.setTimeout(() => {
      setEnterIds((prev) => {
        const next = new Set(prev);
        for (const id of fresh) next.delete(id);
        return next;
      });
    }, 480);
    return () => window.clearTimeout(t);
  }, [messages, loadingThread]);

  useEffect(() => {
    if (!selected?.id || loadingThread) return;
    const t = window.setTimeout(() => setThreadRevealed(true), 30);
    return () => window.clearTimeout(t);
  }, [selected?.id, loadingThread, messages.length]);

  const mentionMembers = useMemo(
    () => groupMentionMembers(selected, colleagues, selfId),
    [selected, colleagues, selfId]
  );
  const mentionLabels = useMemo(
    () => mentionHighlightLabels(selected, colleagues, selfId),
    [selected, colleagues, selfId]
  );
  const mentionQuery =
    selected?.type === "group" ? findMentionQuery(draft, draftCaret) : null;
  const mentionCandidates =
    mentionQuery && !mentionDismissed
      ? filterMentionCandidates(mentionMembers, mentionQuery.query)
      : [];

  useEffect(() => {
    setMentionHi(0);
    setMentionDismissed(false);
  }, [mentionQuery?.start, mentionQuery?.query]);

  const stopVoiceTicker = () => {
    if (voiceTickRef.current != null) {
      window.clearInterval(voiceTickRef.current);
      voiceTickRef.current = null;
    }
  };

  const clearVoicePreview = () => {
    const el = voicePreviewRef.current;
    if (el) {
      el.onended = null;
      el.pause();
      el.removeAttribute("src");
      voicePreviewRef.current = null;
    }
    if (voicePreviewUrlRef.current) {
      URL.revokeObjectURL(voicePreviewUrlRef.current);
      voicePreviewUrlRef.current = null;
    }
    setVoicePreviewing(false);
  };

  const releaseVoiceGraph = async () => {
    stopChatVoiceStream(voiceStreamRef.current);
    voiceStreamRef.current = null;
    const meter = voiceMeterRef.current;
    voiceMeterRef.current = null;
    setVoiceMeter(null);
    try {
      await meter?.close();
    } catch {
      /* ignore */
    }
  };

  const tearDownVoice = () => {
    voiceStartGenRef.current += 1;
    stopVoiceTicker();
    const rec = voiceRecRef.current;
    voiceRecRef.current = null;
    if (rec) {
      rec.onstop = null;
      rec.ondataavailable = null;
      rec.onerror = null;
      if (rec.state !== "inactive") {
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
      }
    }
    void releaseVoiceGraph();
    clearVoicePreview();
    voiceChunksRef.current = [];
    voiceAccumMsRef.current = 0;
    voicePausedRef.current = false;
    setVoiceOn(false);
    setVoicePaused(false);
    setVoiceLocked(false);
    setVoiceMs(0);
    setVoiceBusy(false);
  };

  useEffect(() => {
    return () => tearDownVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const voiceElapsedMs = () =>
    voiceAccumMsRef.current + (voicePausedRef.current ? 0 : Date.now() - voiceStartedAtRef.current);

  const finishVoice = (send: boolean) => {
    const rec = voiceRecRef.current;
    const ext = voiceExtRef.current;
    const elapsed = voiceElapsedMs();
    stopVoiceTicker();
    const flush = () => {
      const chunks = voiceChunksRef.current;
      clearVoicePreview();
      voiceRecRef.current = null;
      voiceChunksRef.current = [];
      setVoiceOn(false);
      setVoicePaused(false);
      setVoiceLocked(false);
      voicePausedRef.current = false;
      voiceAccumMsRef.current = 0;
      setVoiceMs(0);
      if (!send) {
        setVoiceBusy(false);
        void releaseVoiceGraph();
        return;
      }
      void releaseVoiceGraph();
      if (elapsed < CHAT_VOICE_MIN_MS || chunks.length === 0) {
        setVoiceBusy(false);
        onVoiceError("Vocal trop court.");
        return;
      }
      const type = chunks[0]?.type || (ext === "m4a" ? "audio/mp4" : `audio/${ext}`);
      const file = blobToVoiceFile(new Blob(chunks, { type }), ext);
      setVoiceBusy(false);
      onSendVoice(file);
    };
    if (rec && rec.state !== "inactive") {
      rec.onstop = flush;
      try {
        rec.stop();
      } catch {
        flush();
      }
      return;
    }
    flush();
  };

  const startVoice = async () => {
    if (Date.now() < skipVoiceUntilRef.current) return;
    if (voiceOn || voiceBusy) return;
    if (callSession) {
      onVoiceError("Micro occupé par un appel.");
      return;
    }
    if (!chatVoiceSupported()) {
      onVoiceError("L’enregistrement vocal n’est pas disponible sur cet appareil.");
      return;
    }
    onClosePicker();
    onCloseSendEffects();
    const startGen = voiceStartGenRef.current + 1;
    voiceStartGenRef.current = startGen;
    setVoiceBusy(true);
    await playChatVoiceOpen();
    if (voiceStartGenRef.current !== startGen) {
      setVoiceBusy(false);
      return;
    }
    try {
      const { recorder, stream, ext } = await startChatVoiceRecorder();
      if (voiceStartGenRef.current !== startGen) {
        stopChatVoiceStream(stream);
        setVoiceBusy(false);
        return;
      }
      voiceChunksRef.current = [];
      voiceExtRef.current = ext;
      voiceStreamRef.current = stream;
      voiceRecRef.current = recorder;
      const meter = createChatVoiceMeter(stream);
      voiceMeterRef.current = meter;
      setVoiceMeter(meter);
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) voiceChunksRef.current.push(e.data);
      };
      recorder.onerror = () => {
        tearDownVoice();
        onVoiceError("Enregistrement vocal interrompu.");
      };
      recorder.start(250);
      voiceAccumMsRef.current = 0;
      voicePausedRef.current = false;
      voiceStartedAtRef.current = Date.now();
      setVoiceMs(0);
      setVoicePaused(false);
      setVoiceOn(true);
      setVoiceBusy(false);
      voiceTickRef.current = window.setInterval(() => {
        const ms = voiceElapsedMs();
        setVoiceMs(ms);
        if (ms >= CHAT_VOICE_MAX_MS) {
          setVoiceBusy(true);
          finishVoice(true);
        }
      }, 200);
    } catch {
      tearDownVoice();
      onVoiceError("Micro refusé ou indisponible.");
    }
  };

  const toggleVoicePause = () => {
    const rec = voiceRecRef.current;
    if (!rec || voiceBusy || !chatVoicePauseSupported()) return;
    try {
      if (rec.state === "recording") {
        voiceAccumMsRef.current += Date.now() - voiceStartedAtRef.current;
        voicePausedRef.current = true;
        rec.pause();
        if (typeof rec.requestData === "function") rec.requestData();
        setVoicePaused(true);
        setVoiceMs(voiceAccumMsRef.current);
        return;
      }
      if (rec.state === "paused") {
        clearVoicePreview();
        voiceStartedAtRef.current = Date.now();
        voicePausedRef.current = false;
        rec.resume();
        setVoicePaused(false);
      }
    } catch {
      onVoiceError("Pause impossible sur cet appareil.");
    }
  };

  const toggleVoicePreview = () => {
    if (!voicePaused || voiceBusy) return;
    if (voicePreviewing) {
      voicePreviewRef.current?.pause();
      setVoicePreviewing(false);
      return;
    }
    const rec = voiceRecRef.current;
    if (rec && rec.state === "paused" && typeof rec.requestData === "function") {
      rec.requestData();
    }
    window.setTimeout(() => {
      const chunks = voiceChunksRef.current;
      if (chunks.length === 0) {
        onVoiceError("Réécoute pas encore prête.");
        return;
      }
      const ext = voiceExtRef.current;
      const type = chunks[0]?.type || (ext === "m4a" ? "audio/mp4" : `audio/${ext}`);
      const blob = new Blob(chunks, { type });
      if (blob.size < 200) {
        onVoiceError("Réécoute pas encore prête.");
        return;
      }
      clearVoicePreview();
      const url = URL.createObjectURL(blob);
      voicePreviewUrlRef.current = url;
      const el = new Audio(url);
      voicePreviewRef.current = el;
      el.onended = () => setVoicePreviewing(false);
      void el
        .play()
        .then(() => setVoicePreviewing(true))
        .catch(() => onVoiceError("Réécoute impossible sur cet appareil."));
    }, 60);
  };

  const applyMention = (member: ChatColleague) => {
    const next = insertMention(draft, draftCaret, mentionLabel(member));
    onDraftChange(next.text, next.caret);
    setDraftCaret(next.caret);
    requestAnimationFrame(() => {
      const el = draftRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.caret, next.caret);
    });
  };

  const onComposerKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionCandidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionHi((i) => (i + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionHi((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const pick = mentionCandidates[mentionHi] ?? mentionCandidates[0];
        if (pick) applyMention(pick);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionDismissed(true);
        return;
      }
    }
    if (e.key !== "Enter" || e.shiftKey) return;
    const mobileKb =
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse), (max-width: 767px)").matches;
    if (mobileKb) return;
    e.preventDefault();
    void onSend();
  };
  const dmPeerId =
    selected?.type === "dm"
      ? selected.member_ids.map((id) => Number(id)).find((id) => id !== selfId)
      : undefined;
  const colleaguePresence =
    dmPeerId != null ? colleagues.find((c) => Number(c.id) === dmPeerId)?.presence : undefined;
  const peerPresence =
    dmPeerId != null
      ? isChatPeerBusyInCall(dmPeerId, presence, colleaguePresence)
        ? "incall"
        : (presence[dmPeerId] ?? colleaguePresence)
      : undefined;
  const peerInCall = isChatPeerBusyInCall(dmPeerId, presence, colleaguePresence);
  const thisConvLive =
    !!selected?.active_call && selected.active_call.call_id !== suppressJoinCallId;
  const iAmInThisCall =
    !!callSession &&
    !!selected?.active_call &&
    callSession.callId === selected.active_call.call_id;
  const liveJoinableHere = thisConvLive && chatCallIsJoinable(selected?.active_call);
  // DM : correspondant déjà en visio ailleurs → bandeau bloqué (pas Rejoindre).
  // Groupe/salle : visio restreinte (pas dans les cochés).
  const startCallBlocked = Boolean(
    selected?.type === "dm"
      ? peerInCall && !iAmInThisCall && !liveJoinableHere
      : thisConvLive && !liveJoinableHere && !iAmInThisCall
  );
  const selectedClientPill = selected ? conversationClientPillName(selected) : null;
  const showJoin =
    liveJoinableHere && !iAmInThisCall && !callSession && !incomingCall && !startCallBlocked;

  return (
    <section
      ref={threadPaneRef}
      className={`chat-mobile-pane chat-mobile-pane--thread relative flex min-h-0 min-w-0 flex-1 flex-col bg-[var(--chat-bg)] ${
        mobilePane === "thread" ? "is-open" : ""
      }${threadFileDragOver ? " chat-thread-drop-active" : ""}`}
      aria-hidden={mobilePane !== "thread"}
      onDragEnter={selected ? onThreadDragEnter : undefined}
      onDragOver={selected ? onThreadDragOver : undefined}
      onDragLeave={selected ? onThreadDragLeave : undefined}
      onDrop={selected ? onThreadDrop : undefined}
    >
      {selected && threadFileDragOver ? (
        <div className="chat-thread-drop-overlay" aria-hidden>
          <div className="chat-thread-drop-card">
            <Paperclip className="size-6" />
            <p>Déposez pour joindre à la conversation</p>
          </div>
        </div>
      ) : null}
      {selected ? (
        <>
          <header className="flex items-center gap-2 border-b border-[var(--chat-border)] bg-[var(--chat-surface)] px-3 py-2.5">
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg p-2 text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-text)]"
              onClick={onLeaveToSalon}
              aria-label="Retour au salon"
              title="Retour au salon"
            >
              <ArrowLeft className="size-5" />
              <span className="hidden text-xs font-medium md:inline">Salon</span>
            </button>
            <AvatarBubble
              label={conversationTitle(selected, selfId)}
              url={peerAvatarUrl(selected, selfId)}
              presence={
                selected.type === "dm" ? normalizeChatPresence(peerPresence) : undefined
              }
              size="sm"
              onClick={
                peerAvatarUrl(selected, selfId)
                  ? () =>
                      onOpenAvatarPreview(
                        peerAvatarUrl(selected, selfId),
                        conversationTitle(selected, selfId)
                      )
                  : undefined
              }
            />
            <div className="min-w-0 flex-1">
              <h2 className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
                <ConvTypePill type={selected.type} isRoom={selected.is_room} />
                <span className="truncate">{conversationTitle(selected, selfId)}</span>
                {selectedClientPill ? <ClientPill name={selectedClientPill} /> : null}
                {startCallBlocked ? (
                  <span className="chat-incall-chip" title="L’appel n’est pas disponible pour le moment">
                    <Video className="size-3" strokeWidth={2.5} />
                    En visio
                  </span>
                ) : null}
              </h2>
              <p className="truncate text-xs text-[var(--chat-muted)]">
                {typingLabel ||
                  (iAmInThisCall
                    ? "Vous êtes en visio"
                    : startCallBlocked
                      ? "Appel indisponible"
                      : thisConvLive && !callSession
                        ? "Visio en cours"
                        : selected.type === "group"
                          ? `${selected.member_ids.length} participants`
                          : (() => {
                              const p = normalizeChatPresence(peerPresence);
                              if (p === "online") return "En ligne";
                              if (p === "dnd") return "Ne pas déranger";
                              if (p === "meeting") return "En réunion";
                              if (p === "incall") return "En visio";
                              if (dmPeerId != null) {
                                return formatChatLastSeen(lastSeen[dmPeerId]) || "Hors ligne";
                              }
                              return "Discussion";
                            })())}
              </p>
            </div>
            {selected.type === "dm" || selected.type === "group" ? (
              showJoin ? (
                <button
                  type="button"
                  className="chat-join-call-btn rounded-lg px-2.5 py-1.5 text-xs font-semibold"
                  onClick={() =>
                    void onJoinOngoingCall(
                      selected.id,
                      selected.active_call!.call_id,
                      selected.active_call!.media === "audio" ? "audio" : "video",
                      conversationTitle(selected, selfId)
                    )
                  }
                  title="Rejoindre la visio (ou basculer depuis un autre appareil)"
                >
                  <Video className="size-4" />
                  Rejoindre
                </button>
              ) : (
                <button
                  type="button"
                  className="rounded-lg p-2 text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)] disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => void onStartCall("video")}
                  title={
                    startCallBlocked
                      ? "Cette personne est déjà en visio"
                      : selected.is_room || selected.type === "group"
                        ? "Lancer la visio"
                        : "Visio"
                  }
                  disabled={!!callSession || !!incomingCall || startCallBlocked}
                >
                  <Video className="size-5" />
                </button>
              )
            ) : null}
            {selected.type === "dm" && onAuriz ? (
              <button
                type="button"
                className={`rounded-lg p-2 transition ${
                  aurizReady
                    ? "text-[var(--chat-accent-strong)] hover:bg-[var(--chat-accent-soft)]"
                    : "text-[var(--chat-muted)] opacity-40"
                }`}
                disabled={!aurizReady}
                title={aurizTitle || "Auriz"}
                aria-label={aurizTitle || "Auriz"}
                onClick={() => void onAuriz()}
              >
                <Zap className="size-5" strokeWidth={2.3} />
              </button>
            ) : null}
            <button
              type="button"
              className="rounded-lg p-2 text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
              onClick={onToggleInfo}
              title="Infos"
            >
              <Info className="size-5" />
            </button>
          </header>
          {startCallBlocked ? (
            <div className="chat-peer-incall-banner" role="status">
              <span className="chat-peer-incall-banner__icon" aria-hidden>
                <Video className="size-4" strokeWidth={2.25} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">En visio</p>
                <p className="text-xs opacity-80">L’appel n’est pas disponible pour le moment</p>
              </div>
            </div>
          ) : showJoin ? (
            <div className="chat-active-call-banner">
              <span className="chat-active-call-banner__pulse" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">Visio en cours</p>
                <p className="text-xs text-white/70">
                  Rejoindre depuis cet appareil — idéal pour basculer PC → mobile
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-900"
                onClick={() =>
                  void onJoinOngoingCall(
                    selected.id,
                    selected.active_call!.call_id,
                    selected.active_call!.media === "audio" ? "audio" : "video",
                    conversationTitle(selected, selfId)
                  )
                }
              >
                Rejoindre
              </button>
            </div>
          ) : null}

          {!showJoin && selected.type === "group" && meetings[0] ? (
            <div className="chat-meeting-banner">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{meetings[0].title}</p>
                <p className="text-xs text-[var(--chat-muted)]">{formatMeetingWhen(meetings[0].starts_at)}</p>
              </div>
              {meetings[0].joinable && onJoinMeeting ? (
                <button
                  type="button"
                  className="shrink-0 rounded-lg bg-[var(--chat-accent-soft)] px-3 py-1.5 text-xs font-semibold text-[var(--chat-accent-strong)]"
                  onClick={onJoinMeeting}
                >
                  Rejoindre
                </button>
              ) : null}
            </div>
          ) : null}

          <div
            className={`chat-thread-body min-h-0 flex-1${threadRevealed ? " is-revealed" : ""}`}
            data-chat-thread-fx
          >
            {wallpaperPrefs.id !== "none" ? (
              <div
                className="chat-thread-wallpaper"
                data-wallpaper={wallpaperPrefs.id}
                style={{
                  opacity: wallpaperPrefs.opacity / 100,
                  filter:
                    wallpaperPrefs.blur > 0
                      ? `blur(${wallpaperBlurPx(wallpaperPrefs.blur)}px)`
                      : undefined,
                  transform: wallpaperPrefs.blur > 0 ? "scale(1.08)" : undefined,
                  ...(wallpaperPrefs.id === "custom" && wallpaperPrefs.customUrl
                    ? {
                        backgroundImage: `url(${wallpaperPrefs.customUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }
                    : {}),
                }}
                aria-hidden
              />
            ) : null}
            <div
              ref={threadScrollRef}
              className="chat-thread-scroll space-y-1.5 px-2.5 py-3 md:px-5"
            >
              {loadingThread && messages.length === 0 ? (
                <div className="chat-thread-skel" aria-hidden>
                  <span className="chat-thread-skel__bubble chat-thread-skel__bubble--them" />
                  <span className="chat-thread-skel__bubble chat-thread-skel__bubble--me" />
                  <span className="chat-thread-skel__bubble chat-thread-skel__bubble--them chat-thread-skel__bubble--short" />
                  <span className="chat-thread-skel__bubble chat-thread-skel__bubble--me chat-thread-skel__bubble--mid" />
                </div>
              ) : messages.length === 0 ? (
                <p className="text-center text-sm text-[var(--chat-muted)]">
                  Aucun message — écrivez le premier.
                </p>
              ) : (
                messages.map((m, index) => {
                  const dayLabel = formatChatThreadDayLabel(
                    index > 0 ? messages[index - 1]?.created_at : null,
                    m.created_at
                  );
                  const dayChip = dayLabel ? (
                    <div className="chat-day-sep" role="separator">
                      <span>{dayLabel}</span>
                    </div>
                  ) : null;
                  if (
                    m.sender_id === 0 ||
                    isGuestLobbyBody(m.body || "") ||
                    isMeetingReminderBody(m.body || "")
                  ) {
                    return (
                      <Fragment key={m.id}>
                        {dayChip}
                        <p className="px-3 py-2 text-center text-xs font-medium text-[var(--chat-muted)]">
                          {m.body}
                        </p>
                      </Fragment>
                    );
                  }
                  const mine = m.sender_id === selfId;
                  const voiceFace = voiceMessageFace(
                    m,
                    selfId,
                    selfAvatarUrl,
                    selfLabel,
                    colleagues,
                    selected
                  );
                  const editing = editingMsgId === m.id;
                  const hasReactions = (m.reactions?.length ?? 0) > 0;
                  const loneMediaUrl = !editing ? extractLoneMediaUrl(m.body) : null;
                  const isMediaMsg =
                    Boolean(loneMediaUrl) ||
                    (!editing && (isImageOnlyMessage(m) || isVideoOnlyMessage(m)));
                  const imageAtts = (m.attachments ?? []).filter(isImageAttachment);
                  const videoAtts = (m.attachments ?? []).filter(isVideoAttachment);
                  const otherAtts = (m.attachments ?? []).filter(
                    (a) => !isImageAttachment(a) && !isVideoAttachment(a)
                  );
                  const captionText = (m.body || "").trim();
                  const isCaptionedImage =
                    !editing &&
                    !isMediaMsg &&
                    (imageAtts.length > 0 || videoAtts.length > 0) &&
                    Boolean(captionText) &&
                    !/^\[[^\]]+\]$/.test(captionText);

                  const bubbleHandlers = {
                    onContextMenu: (e: MouseEvent) => {
                      const selectedText = window.getSelection?.()?.toString().trim();
                      if (selectedText) return;
                      e.preventDefault();
                      // Mobile : l’appui long ouvre déjà le tiroir (évite le menu natif + un 2e menu)
                      if (
                        typeof window !== "undefined" &&
                        window.matchMedia("(pointer: coarse), (max-width: 767px)").matches
                      ) {
                        return;
                      }
                      onOpenMsgMenu(m, e.clientX, e.clientY);
                    },
                    onPointerDown: (e: PointerEvent) => onBubblePointerDown(m, e),
                    onPointerMove: onBubblePointerMove,
                    onPointerUp: onClearLongPress,
                    onPointerCancel: onClearLongPress,
                    onPointerLeave: (e: PointerEvent) => {
                      if (e.pointerType !== "mouse") onClearLongPress();
                    },
                  };

                  const metaRow = (
                    <div
                      className={`flex items-center justify-end gap-1 text-[9px] leading-none text-[var(--chat-muted)] ${
                        isMediaMsg
                          ? "absolute bottom-1.5 right-2 rounded-full bg-black/45 px-1.5 py-0.5 text-white"
                          : "mt-0.5"
                      }`}
                    >
                      {m.edited ? <span className="italic opacity-80">modifié</span> : null}
                      <span>{formatChatTime(m.created_at)}</span>
                      {mine ? (
                        m.pending ? (
                          <span className="chat-msg-sending-dot" title="Envoi…" aria-label="Envoi…" />
                        ) : (
                          <MessageTicks receipt={m.receipt} />
                        )
                      ) : null}
                    </div>
                  );

                  const bodyInner = editing ? (
                    <div className="space-y-2 px-2.5 py-1.5">
                      <textarea
                        value={editDraft}
                        onChange={(e) => {
                          const caret = e.target.selectionStart ?? e.target.value.length;
                          const expanded = expandEmoticonsWithCaret(e.target.value, caret);
                          onEditDraftChange(expanded.text);
                          if (expanded.text !== e.target.value) {
                            const el = e.target;
                            const pos = expanded.caret;
                            requestAnimationFrame(() => el.setSelectionRange(pos, pos));
                          }
                        }}
                        rows={3}
                        className="w-full rounded-lg border border-[var(--chat-border)] bg-[var(--chat-bg)] px-2 py-1.5 text-[13px] text-[var(--chat-fg)] outline-none"
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="text-xs text-[var(--chat-muted)]"
                          onClick={onCancelEdit}
                        >
                          Annuler
                        </button>
                        <button
                          type="button"
                          className="text-xs font-medium text-[var(--chat-accent-strong)]"
                          onClick={() => void onSaveEdit()}
                        >
                          Enregistrer
                        </button>
                      </div>
                    </div>
                  ) : isChatPollMessage(m) ? (
                    <div className="px-0.5">
                      <ChatPollCard
                        poll={messagePoll(m)!}
                        selfId={selfId}
                        colleagues={colleagues}
                        canClose={mine && !messagePoll(m)!.closed}
                        onVote={(optionId) => onVotePoll(m.id, optionId)}
                        onClose={() => onClosePoll(m.id)}
                      />
                    </div>
                  ) : (
                    <>
                      {loneMediaUrl ? (
                        <button
                          type="button"
                          className="chat-msg-gif-hit cursor-zoom-in"
                          onClick={() => onOpenMediaLightbox(loneMediaUrl, undefined, { url: loneMediaUrl, name: "image.gif", mime: "image/gif" })}
                        >
                          <img src={loneMediaUrl} alt="" className="chat-msg-gif" loading="lazy" />
                        </button>
                      ) : m.body && !isMediaMsg ? (
                        <>
                          <ChatMessageText text={m.body} mentionLabels={mentionLabels} />
                          {selected.type === "dm" && isCallLogBody(m.body) ? (
                            <button
                              type="button"
                              className="chat-recall-btn mt-1.5 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold"
                              disabled={!!callSession || !!incomingCall || startCallBlocked}
                              onClick={() => void onStartCall("video")}
                            >
                              <Video className="size-3.5" />
                              Rappeler
                            </button>
                          ) : null}
                        </>
                      ) : null}
                      {m.attachments?.map((a, i) =>
                        isImageAttachment(a) ? (
                          <button
                            key={`${m.id}-att-${i}`}
                            type="button"
                            className={`chat-msg-gif-hit cursor-zoom-in ${isMediaMsg ? "" : "mt-1"}`}
                            onClick={() => onOpenMediaLightbox(a.url, a.name, a)}
                          >
                            <img src={a.url} alt={a.name} className="chat-msg-gif" loading="lazy" />
                          </button>
                        ) : isVideoAttachment(a) ? (
                          <video
                            key={`${m.id}-att-${i}`}
                            className={`chat-msg-video ${isMediaMsg ? "" : "mt-1"}`}
                            src={a.url}
                            controls
                            playsInline
                            preload="metadata"
                          />
                        ) : isAudioAttachment(a) ? (
                          <ChatVoicePlayer
                            key={`${m.id}-att-${i}`}
                            url={a.url}
                            mime={a.mime}
                            mine={mine}
                            avatarUrl={voiceFace.url}
                            avatarLabel={voiceFace.label}
                            onAvatarClick={
                              voiceFace.url
                                ? () => onOpenAvatarPreview(voiceFace.url, voiceFace.label)
                                : undefined
                            }
                          />
                        ) : (
                          <a
                            key={`${m.id}-att-${i}`}
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 block text-[11px] underline opacity-80"
                          >
                            {a.name}
                          </a>
                        )
                      )}
                    </>
                  );

                  const groupSender =
                    !mine && selected.type === "group" ? (
                      <div
                        className={`text-[10px] font-medium text-[var(--chat-accent-strong)] ${
                          isMediaMsg ? "px-2.5 pt-1.5 pb-1" : "mb-0.5"
                        }`}
                      >
                        {memberLabel(
                            colleagues.find((c) => c.id === m.sender_id),
                            m.sender_name || `#${m.sender_id}`
                          )}
                      </div>
                    ) : null;

                  const reactionChips = (
                    <MessageReactionChips
                      reactions={m.reactions}
                      selfId={selfId}
                      align={mine ? "end" : "start"}
                      onToggle={(emoji) => void onReact(m.id, emoji)}
                    />
                  );

                  const replyPrev = resolveReplyPreview(m);
                  const replyQuoteEl = replyPrev ? (
                    <ChatReplyQuote
                      author={
                        replyPrev.sender_id === selfId
                          ? "Vous"
                          : memberLabel(
                              colleagues.find((c) => c.id === replyPrev.sender_id),
                              replyPrev.sender_name?.trim() || `#${replyPrev.sender_id}`
                            )
                      }
                      snippet={chatReplySnippet(replyPrev.body, {
                        hasVoice: /^message vocal$/i.test(replyPrev.body || ""),
                      })}
                      mine={mine}
                      onClick={() => onScrollToMessage(replyPrev.id)}
                    />
                  ) : null;

                  const jumboEmoji = Boolean(m.body && loneMessageEmojis(m.body));
                  const textBubble = (
                    <div
                      className={`chat-msg-bubble relative w-fit max-w-full ${
                        jumboEmoji
                          ? "chat-msg-bubble--jumbo"
                          : `rounded-xl px-2.5 py-1.5 shadow-sm ${
                              mine ? "chat-bubble-mine rounded-br-sm" : "chat-bubble-other rounded-bl-sm"
                            }`
                      }`}
                      {...bubbleHandlers}
                    >
                      {groupSender}
                      {replyQuoteEl}
                      {bodyInner}
                      {metaRow}
                    </div>
                  );

                  const effect =
                    m.effect === "impact" || m.effect === "echo"
                      ? m.effect
                      : null;

                  return (
                    <Fragment key={m.id}>
                    {dayChip}
                    <div
                      data-chat-msg-id={m.id}
                      className={`flex ${mine ? "justify-end" : "justify-start"} ${
                        hasReactions || effect ? "mb-3.5" : ""
                      } ${flashReplyId === m.id ? "chat-msg-row--flash" : ""}${
                        enterIds.has(m.id) ? (mine ? " chat-msg-enter chat-msg-enter--mine" : " chat-msg-enter") : ""
                      }${m.pending ? " chat-msg-pending" : ""}`}
                    >
                      {isCaptionedImage ? (
                        <div className="relative inline-block max-w-full">
                          <div className={`chat-msg-hover-wrap ${mine ? "chat-msg-hover-wrap--mine" : ""}`}>
                            <div
                              className={`chat-msg-bubble chat-msg-bubble--with-photo relative select-none overflow-hidden rounded-2xl shadow-sm ${
                                mine ? "chat-bubble-mine" : "chat-bubble-other"
                              }`}
                              {...bubbleHandlers}
                            >
                              {groupSender ? (
                                <div className="px-2.5 pt-1.5">{groupSender}</div>
                              ) : null}
                              {replyQuoteEl ? <div className="px-2.5 pt-2">{replyQuoteEl}</div> : null}
                              <div className="chat-msg-with-photo__media">
                                {imageAtts.map((a, i) => (
                                  <button
                                    key={`${m.id}-cap-${i}`}
                                    type="button"
                                    className="chat-msg-gif-hit cursor-zoom-in"
                                    onClick={() => onOpenMediaLightbox(a.url, a.name, a)}
                                  >
                                    <img src={a.url} alt={a.name} className="chat-msg-gif" loading="lazy" />
                                  </button>
                                ))}
                                {videoAtts.map((a, i) => (
                                  <video
                                    key={`${m.id}-capv-${i}`}
                                    className="chat-msg-video"
                                    src={a.url}
                                    controls
                                    playsInline
                                    preload="metadata"
                                  />
                                ))}
                              </div>
                              <div className="chat-msg-with-photo__caption">
                                <ChatMessageText text={m.body} mentionLabels={mentionLabels} />
                                {otherAtts.map((a, i) =>
                                  isAudioAttachment(a) ? (
                                    <ChatVoicePlayer
                                      key={`${m.id}-oa-${i}`}
                                      url={a.url}
                                      mime={a.mime}
                                      mine={mine}
                                      avatarUrl={voiceFace.url}
                                      avatarLabel={voiceFace.label}
                                      onAvatarClick={
                                        voiceFace.url
                                          ? () => onOpenAvatarPreview(voiceFace.url, voiceFace.label)
                                          : undefined
                                      }
                                    />
                                  ) : (
                                    <a
                                      key={`${m.id}-oa-${i}`}
                                      href={a.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="mt-1 block text-[11px] underline opacity-80"
                                    >
                                      {a.name}
                                    </a>
                                  )
                                )}
                                {metaRow}
                              </div>
                            </div>
                            {!editing ? (
                              <MessageHoverActions
                                mine={mine}
                                myReaction={myReactionOn(m)}
                                onReply={() => onBeginReply(m)}
                                onCopy={() => onCopyMessage(m)}
                                onForward={() => onForwardMessage(m)}
                                onReact={(emoji) => void onReact(m.id, emoji)}
                              />
                            ) : null}
                          </div>
                          {reactionChips}
                        </div>
                      ) : isMediaMsg ? (
                        <div className="relative inline-block max-w-full">
                          <div className={`chat-msg-hover-wrap ${mine ? "chat-msg-hover-wrap--mine" : ""}`}>
                            <div
                              className="chat-msg-bubble chat-msg-bubble--media relative select-none overflow-hidden rounded-2xl p-0 shadow-md"
                              {...bubbleHandlers}
                            >
                              {groupSender}
                              {replyQuoteEl ? <div className="px-2.5 pt-2">{replyQuoteEl}</div> : null}
                              {bodyInner}
                              {metaRow}
                            </div>
                            {!editing ? (
                              <MessageHoverActions
                                mine={mine}
                                myReaction={myReactionOn(m)}
                                onReply={() => onBeginReply(m)}
                                onCopy={() => onCopyMessage(m)}
                                onForward={() => onForwardMessage(m)}
                                onReact={(emoji) => void onReact(m.id, emoji)}
                              />
                            ) : null}
                          </div>
                          {reactionChips}
                        </div>
                      ) : (
                        <div className="relative max-w-[82%] md:max-w-[62%]">
                          <div className={`chat-msg-hover-wrap ${mine ? "chat-msg-hover-wrap--mine" : ""}`}>
                            {effect ? (
                              <ChatEffectBubbleWrap
                                messageId={m.id}
                                effect={effect}
                                mine={mine}
                                echoText={m.body}
                                playToken={effectReplay[m.id] ?? 0}
                                onReplay={() => onReplayEffect(m.id)}
                              >
                                {textBubble}
                              </ChatEffectBubbleWrap>
                            ) : (
                              textBubble
                            )}
                            {!editing ? (
                              <MessageHoverActions
                                mine={mine}
                                myReaction={myReactionOn(m)}
                                onReply={() => onBeginReply(m)}
                                onCopy={() => onCopyMessage(m)}
                                onForward={() => onForwardMessage(m)}
                                onReact={(emoji) => void onReact(m.id, emoji)}
                              />
                            ) : null}
                          </div>
                          {reactionChips}
                        </div>
                      )}
                    </div>
                    </Fragment>
                  );
                })
              )}
              {typingLabel ? <TypingIndicator label={typingLabel} /> : null}
              <div ref={threadEndRef} />
            </div>
          </div>

          {sending ? <div className="chat-send-rail" aria-hidden /> : null}

          {otherAttach.length > 0 ? (
            <div className="chat-attach-strip flex flex-wrap gap-2 border-t border-[var(--chat-border)] bg-[var(--chat-surface-elevated)] px-3 py-2">
              {otherAttach.map(({ file, index }) => (
                <ComposerAttachPreview
                  key={`${file.name}-${file.size}-${index}`}
                  file={file}
                  onRemove={() => {
                    skipVoiceUntilRef.current = Date.now() + 500;
                    onRemoveFile(index);
                  }}
                />
              ))}
            </div>
          ) : null}

          <ChatAttachSourceModal
            open={attachSourceOpen}
            onClose={() => setAttachSourceOpen(false)}
            onPickGallery={() => {
              setAttachSourceOpen(false);
              window.setTimeout(() => galleryInputRef.current?.click(), 0);
            }}
            onPickDevice={() => {
              setAttachSourceOpen(false);
              window.setTimeout(() => fileInputRef.current?.click(), 0);
            }}
            onPickCoffre={() => {
              setAttachSourceOpen(false);
              setAttachPicker("coffre");
            }}
            onPickPhotos={() => {
              setAttachSourceOpen(false);
              setAttachPicker("photos");
            }}
            onPickPoll={() => {
              setAttachSourceOpen(false);
              setPollComposerOpen(true);
            }}
          />
          <ChatPollComposer
            open={pollComposerOpen}
            busy={pollComposerBusy}
            error={pollComposerError}
            onClose={() => setPollComposerOpen(false)}
            onSubmit={(question, options) => {
              onCreatePoll(question, options);
              setPollComposerOpen(false);
            }}
          />
          <ChatAttachPickerModal
            open={attachPicker != null}
            kind={attachPicker ?? "coffre"}
            onClose={() => setAttachPicker(null)}
            onConfirm={(list) => {
              setAttachPicker(null);
              onAddComposerFiles(list);
            }}
          />
          {filterFile ? (
            <ChatPhotoFilterSheet
              file={filterFile}
              onCancel={() => setFilterFile(null)}
              onSend={async (out, caption) => {
                const ok = await onSendCapturedPhoto(out, caption);
                if (ok !== false) setFilterFile(null);
                return ok;
              }}
            />
          ) : null}
          {videoFile ? (
            <ChatVideoComposeSheet
              file={videoFile}
              onCancel={() => setVideoFile(null)}
              onSend={async (out, caption) => {
                const ok = await onSendCapturedPhoto(out, caption);
                if (ok !== false) setVideoFile(null);
                return ok;
              }}
            />
          ) : null}
          <ChatCaptureSheet
            open={captureOpen}
            onClose={() => setCaptureOpen(false)}
            onPhoto={(file) => {
              setCaptureOpen(false);
              setFilterFile(file);
            }}
            onVideo={(file) => {
              setCaptureOpen(false);
              setVideoFile(file);
            }}
            onFallback={() => {
              setCaptureOpen(false);
              window.setTimeout(() => cameraInputRef.current?.click(), 0);
            }}
          />
          <footer className="relative border-t border-[var(--chat-border)] bg-[var(--chat-surface)] px-2.5 py-2.5 sm:px-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const list = e.target.files ? [...e.target.files] : [];
                e.target.value = "";
                onAddComposerFiles(list);
              }}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const list = e.target.files ? [...e.target.files] : [];
                e.target.value = "";
                const images = list.filter(isComposerImageFile);
                if (images.length) onAddComposerFiles(images);
              }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const list = e.target.files ? [...e.target.files] : [];
                e.target.value = "";
                const photo = list.length === 1 ? list[0] : null;
                if (photo && isFilterableChatPhoto(photo)) {
                  setFilterFile(photo);
                  return;
                }
                if (list.length) onAddComposerFiles(list);
              }}
            />
            {showPicker ? (
              <div className="absolute bottom-[calc(100%+0.4rem)] left-2.5 z-30 right-2.5 sm:right-auto">
                <ChatEmojiGifPicker
                  suiteUserId={suiteUserId}
                  onEmoji={onEmoji}
                  onGif={(g) => void onSendGif(g)}
                  onClose={onClosePicker}
                />
              </div>
            ) : mentionCandidates.length > 0 ? (
              <div className="chat-mention-drawer-anchor">
                <ChatMentionPicker
                  candidates={mentionCandidates}
                  highlightIndex={mentionHi}
                  presence={presence}
                  onPick={applyMention}
                  onHighlight={setMentionHi}
                  onClose={() => setMentionDismissed(true)}
                />
              </div>
            ) : null}
            {imageAttach.length > 0 ? (
              <div className="chat-composer-caption">
                {imageAttach.map(({ file, index }) => (
                  <ComposerAttachPreview
                    key={`${file.name}-${file.size}-${index}`}
                    file={file}
                    variant="caption"
                    onRemove={() => {
                      skipVoiceUntilRef.current = Date.now() + 500;
                      onRemoveFile(index);
                    }}
                  />
                ))}
              </div>
            ) : null}
            {replyTo ? (
              <ChatReplyComposerBar
                author={replyAuthorLabel(replyTo)}
                snippet={chatReplySnippet(replyTo.body, {
                  hasImage: isImageOnlyMessage(replyTo) || Boolean(extractLoneMediaUrl(replyTo.body)),
                  hasVoice: (replyTo.attachments ?? []).some(isAudioAttachment),
                  hasFile: (replyTo.attachments?.length ?? 0) > 0,
                })}
                onCancel={onCancelReply}
              />
            ) : null}
            <div className="flex items-end gap-2">
              {voiceOn || voiceBusy ? (
                <div className="chat-voice-rec flex min-w-0 flex-1 items-center gap-2 rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-2 py-1">
                  <button
                    type="button"
                    className="chat-composer__btn shrink-0 text-[var(--chat-danger)] hover:bg-[var(--chat-surface-elevated)]"
                    title="Annuler"
                    aria-label="Annuler l’enregistrement"
                    disabled={voiceOn ? voiceBusy : false}
                    onClick={() => (voiceOn ? finishVoice(false) : tearDownVoice())}
                  >
                    <Trash2 className="size-[1.15rem]" />
                  </button>
                  <span
                    className={`chat-voice-rec__dot ${voicePaused ? "chat-voice-rec__dot--paused" : ""}`}
                    aria-hidden
                  />
                  <span className="text-sm font-medium tabular-nums text-[var(--chat-text)]">
                    {formatVoiceClock(voiceMs)}
                  </span>
                  {voiceMeter ? (
                    <ChatVoiceLiveWave meter={voiceMeter} paused={voicePaused} />
                  ) : (
                    <span className="min-w-0 truncate text-xs text-[var(--chat-muted)]">
                      {voicePaused ? "En pause" : "Enregistrement…"}
                    </span>
                  )}
                  {voicePaused ? (
                    <button
                      type="button"
                      className="chat-composer__btn shrink-0 text-[var(--chat-text)] hover:bg-[var(--chat-surface-elevated)]"
                      title={voicePreviewing ? "Arrêter l’écoute" : "Réécouter"}
                      aria-label={voicePreviewing ? "Arrêter l’écoute" : "Réécouter avant envoi"}
                      disabled={voiceBusy}
                      onClick={toggleVoicePreview}
                    >
                      {voicePreviewing ? (
                        <Pause className="size-[1.15rem]" />
                      ) : (
                        <Play className="size-[1.15rem]" />
                      )}
                    </button>
                  ) : null}
                  {chatVoicePauseSupported() ? (
                    <button
                      type="button"
                      className="chat-composer__btn shrink-0 text-[var(--chat-text)] hover:bg-[var(--chat-surface-elevated)]"
                      title={voicePaused ? "Reprendre" : "Pause"}
                      aria-label={voicePaused ? "Reprendre l’enregistrement" : "Mettre en pause"}
                      disabled={voiceBusy}
                      onClick={toggleVoicePause}
                    >
                      {voicePaused ? (
                        <Mic className="size-[1.15rem]" />
                      ) : (
                        <Pause className="size-[1.15rem]" />
                      )}
                    </button>
                  ) : null}
                </div>
              ) : (
              <div className="chat-composer flex min-w-0 flex-1 items-end gap-0.5 rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-bg)] px-1 py-1 focus-within:border-[var(--chat-accent)]">
                <div className={`chat-composer__tools ${toolsOpen ? "is-open" : ""}`}>
                  <button
                    type="button"
                    className="chat-composer__btn chat-composer__btn--tight shrink-0 text-[var(--chat-muted)] transition hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-text)]"
                    title={toolsOpen ? "Réduire" : "Photo et ajouter"}
                    aria-label={toolsOpen ? "Réduire les actions" : "Photo et ajouter"}
                    aria-expanded={toolsOpen}
                    onClick={() => setToolsOpen((v) => !v)}
                  >
                    {toolsOpen ? (
                      <ChevronLeft className="size-[1.15rem]" />
                    ) : (
                      <ChevronRight className="size-[1.15rem]" />
                    )}
                  </button>
                  <div className="chat-composer__tools-drawer">
                    <button
                      type="button"
                      className="chat-composer__btn chat-composer__btn--tight shrink-0 text-[var(--chat-muted)] transition hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-text)]"
                      title="Appareil photo"
                      aria-label="Appareil photo"
                      tabIndex={toolsOpen ? 0 : -1}
                      onClick={() => setCaptureOpen(true)}
                    >
                      <Camera className="size-[1.15rem]" />
                    </button>
                    <button
                      type="button"
                      className="chat-composer__btn chat-composer__btn--tight shrink-0 text-[var(--chat-muted)] transition hover:bg-[var(--chat-surface-elevated)] hover:text-[var(--chat-text)]"
                      title="Ajouter"
                      aria-label="Ajouter"
                      tabIndex={toolsOpen ? 0 : -1}
                      onClick={() => setAttachSourceOpen(true)}
                    >
                      <Plus className="size-[1.15rem]" />
                    </button>
                  </div>
                </div>
                <textarea
                  ref={draftRef}
                  value={draft}
                  onChange={(e) => {
                    const caret = e.target.selectionStart ?? e.target.value.length;
                    setDraftCaret(caret);
                    onDraftChange(e.target.value, caret);
                  }}
                  onSelect={(e) => {
                    const el = e.currentTarget;
                    setDraftCaret(el.selectionStart ?? el.value.length);
                  }}
                  onKeyUp={(e) => {
                    const el = e.currentTarget;
                    setDraftCaret(el.selectionStart ?? el.value.length);
                  }}
                  onKeyDown={onComposerKeyDown}
                  rows={1}
                  placeholder={
                    hasImageAttach ? "Ajouter une légende…" : replyTo ? "Répondre…" : "Message…"
                  }
                  className="chat-composer__input max-h-32 min-w-0 flex-1 resize-none bg-transparent text-sm outline-none placeholder:text-[var(--chat-muted)]"
                />
                <button
                  type="button"
                  className={`chat-composer__btn shrink-0 transition hover:bg-[var(--chat-surface-elevated)] ${
                    showPicker
                      ? "bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]"
                      : "text-[var(--chat-muted)] hover:text-[var(--chat-text)]"
                  }`}
                  title="Émojis et GIF"
                  aria-expanded={showPicker}
                  onClick={onTogglePicker}
                >
                  <SmilePlus className="size-[1.15rem]" />
                </button>
              </div>
              )}
              <div className="relative shrink-0" data-chat-send-effects>
                <ChatSendEffectsMenu
                  open={sendEffectsOpen}
                  disabled={!draft.trim() || voiceOn}
                  onClose={onCloseSendEffects}
                  onPick={(effect) => void onSend(effect)}
                />
                {!voiceLocked && !draft.trim() && files.length === 0 ? (
                  <ChatVoiceHoldMic
                    disabled={false}
                    ready={voiceOn}
                    locked={voiceLocked}
                    onHoldStart={() => {
                      if (Date.now() < skipVoiceUntilRef.current) return;
                      void startVoice();
                    }}
                    onLocked={() => {
                      if (Date.now() < skipVoiceUntilRef.current && !voiceOn && !voiceBusy) {
                        return;
                      }
                      setVoiceLocked(true);
                    }}
                    onCancel={() => finishVoice(false)}
                  />
                ) : (
                <button
                  type="button"
                  className="chat-composer__send shrink-0 rounded-full bg-[var(--chat-accent)] text-[#0f172a] shadow-sm transition hover:brightness-105 disabled:opacity-40"
                  disabled={voiceBusy}
                  aria-label={voiceOn ? "Envoyer le vocal" : "Envoyer — appui long pour un effet"}
                  title={voiceOn ? "Envoyer le vocal" : "Envoyer · appui long = effets"}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    if (voiceOn) return;
                    sendLongPressFired.current = false;
                    if (sendLongPressTimer.current) {
                      window.clearTimeout(sendLongPressTimer.current);
                    }
                    sendLongPressTimer.current = window.setTimeout(() => {
                      sendLongPressFired.current = true;
                      onOpenSendEffects();
                    }, 420);
                  }}
                  onPointerUp={() => {
                    if (sendLongPressTimer.current) {
                      window.clearTimeout(sendLongPressTimer.current);
                      sendLongPressTimer.current = null;
                    }
                  }}
                  onPointerCancel={() => {
                    if (sendLongPressTimer.current) {
                      window.clearTimeout(sendLongPressTimer.current);
                      sendLongPressTimer.current = null;
                    }
                  }}
                  onPointerLeave={() => {
                    if (sendLongPressTimer.current) {
                      window.clearTimeout(sendLongPressTimer.current);
                      sendLongPressTimer.current = null;
                    }
                  }}
                  onClick={() => {
                    if (sendLongPressFired.current) {
                      sendLongPressFired.current = false;
                      return;
                    }
                    if (voiceOn) {
                      setVoiceBusy(true);
                      finishVoice(true);
                      return;
                    }
                    void onSend();
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (!voiceOn) onOpenSendEffects();
                  }}
                >
                  <Send className="size-5" />
                </button>
                )}
              </div>
            </div>
          </footer>
        </>
      ) : (
        <div className="chat-empty-hero flex flex-1 flex-col items-center justify-center gap-4 bg-[var(--chat-bg)] px-6 text-center">
          <ChatBrandMark size="lg" />
          <div className="space-y-1.5">
            <p className="text-sm font-medium text-[var(--chat-text)]">Sélectionnez une conversation</p>
            <p className="max-w-sm text-xs text-[var(--chat-muted)]">
              Ou démarrez un message, un groupe ou une visio depuis l’accueil.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 pt-1">
            <button type="button" className="chat-home-cta chat-home-cta--primary" onClick={onOpenNewDm}>
              <MessageCirclePlus className="size-3.5" />
              Nouveau message
            </button>
            <button type="button" className="chat-home-cta" onClick={onOpenNewGroup}>
              <Users className="size-3.5" />
              Nouveau groupe
            </button>
            <button
              type="button"
              className="chat-home-cta chat-home-visio-btn"
              onClick={onOpenNewVideo}
              disabled={!!callSession || !!incomingCall}
            >
              <Video className="size-3.5" />
              Nouvelle visio
              <span className="chat-new-badge" aria-hidden>
                Nouveau
              </span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function ComposerAttachPreview({
  file,
  onRemove,
  variant = "chip",
}: {
  file: File;
  onRemove: () => void;
  variant?: "chip" | "caption";
}) {
  const isImage = isComposerImageFile(file);
  const url = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage]);
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  const removeBtn = (
    <button
      type="button"
      aria-label="Retirer"
      className={
        url
          ? "absolute right-0.5 top-0.5 inline-flex size-5 items-center justify-center rounded-full bg-black/55 text-white"
          : undefined
      }
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        window.setTimeout(onRemove, 0);
      }}
    >
      <X className={url ? "size-3" : "size-3.5"} />
    </button>
  );

  if (url) {
    return (
      <span
        className={
          variant === "caption"
            ? "chat-composer-caption__thumb chat-attach-chip relative inline-block overflow-hidden"
            : "chat-attach-chip relative inline-block overflow-hidden rounded-xl border border-[var(--chat-border)]"
        }
      >
        <img
          src={url}
          alt=""
          className={variant === "caption" ? "chat-composer-caption__img" : "size-[4.25rem] object-cover"}
        />
        {removeBtn}
      </span>
    );
  }

  return (
    <span className="chat-attach-chip inline-flex items-center gap-1 rounded-full border border-[var(--chat-border)] bg-[var(--chat-surface)] px-2 py-1 text-xs">
      {file.name}
      <button
        type="button"
        aria-label="Retirer"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          window.setTimeout(onRemove, 0);
        }}
      >
        <X className="size-3.5" />
      </button>
    </span>
  );
}
